import api from "./api";

/**
 * Role name (as stored in the `role` table) allowed to broadcast one room to
 * every LGA of its own state. Kept here so the studio button and the CM login
 * allow-list read the same constant instead of two drifting string literals.
 */
export const STATE_BROADCAST_ROLE = "state broadcaster";

/**
 * The selective variant: broadcasts from its own LGA and picks which other
 * LGAs of its own state receive it, with a select-all shortcut.
 */
export const LGA_BROADCAST_ROLE = "lga broadcaster";

export const BROADCAST_ROLES = [STATE_BROADCAST_ROLE, LGA_BROADCAST_ROLE];

const currentRole = () =>
  (localStorage.getItem("isAdminValue") || "").trim().toLowerCase();

/** True when the currently logged-in user holds the all-LGAs-only role. */
export const isStateBroadcaster = () => currentRole() === STATE_BROADCAST_ROLE;

/** True when the currently logged-in user may pick which LGAs to reach. */
export const isLgaBroadcaster = () => currentRole() === LGA_BROADCAST_ROLE;

/** True for either broadcast role. */
export const isAnyBroadcaster = () => BROADCAST_ROLES.includes(currentRole());

/**
 * Every call resolves to `{ success, data | error }` rather than throwing, so a
 * failure here can never take down the broadcast page around it — the caller
 * just carries on with the normal per-LGA behaviour.
 */
const safeCall = async (fn) => {
  try {
    const response = await fn();
    const body = response?.data || {};
    if (body.statusCode === 200) {
      return { success: true, data: body.data, message: body.message };
    }
    return { success: false, message: body.message || "Request failed" };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || "Request failed",
    };
  }
};

export const stateBroadcastService = {
  /**
   * Start broadcasting `roomName` across the caller's own state.
   *
   * With no options this reaches every LGA — which is the only thing the
   * state-broadcaster role can do, and the exact call it has always made.
   * The lga-broadcaster role passes `{ lgaNames }` to reach a subset, or
   * `{ selectAll: true }` for the whole state.
   */
  start: (roomName, options = {}) =>
    safeCall(() => api.post("/state-broadcast/start", { roomName, ...options })),

  /** LGAs of the caller's own state, for the selective picker. */
  lgas: () => safeCall(() => api.get("/state-broadcast/lgas")),

  /** Stop the caller's state-wide broadcast; viewers fall back to their own LGA rooms. */
  stop: (roomName) =>
    safeCall(() => api.post("/state-broadcast/stop", { roomName })),

  /** Current state-wide status for the caller's own state (restores button state after refresh). */
  status: () => safeCall(() => api.get("/state-broadcast/status")),

  /**
   * Viewer-side: given the room for the state/LGA a viewer picked, find out
   * whether that state has a state-wide broadcast running from another room.
   */
  resolve: (roomName) =>
    safeCall(() => api.get(`/state-broadcast/resolve/${encodeURIComponent(roomName)}`)),
};

export default stateBroadcastService;
